import { useCallback, useEffect, useReducer, useRef } from "react";
import { chatReducer, initialChatState, latestAssistant, type ChatState } from "./chat-state";
import { runChatStream, type HeadersSource, type PollOptions, type StreamResult } from "./stream-client";
import type { CardState, ChatAction, ChatEvent, ChatMessage, Citation, ErrorPayload, ResultCard, StreamStatus } from "./types";
import type { WorkflowReply } from "./workflow/contract";

export interface BuildBodyContext {
  text: string;
  conversationId: string | null;
  /** Idempotency key for this run (send as `client_message_id`). */
  clientMessageId: string;
  /** Set when this turn answers a workflow card: send it as `workflow_reply` (spread `workflowRequestFields(reply)` into the body). */
  workflowReply?: WorkflowReply;
  /** Single-use bot-check token of this turn (a "Send code" / "Resend code" answer); send it as `turnstile_token`. */
  turnstileToken?: string;
}

export interface SendOptions {
  /** The turn answers a workflow card; `text` is the visible message (button label / entered value). */
  workflowReply?: WorkflowReply;
  /** The answer asks the server to e-mail a code: the bot-check token that goes with it. */
  turnstileToken?: string;
}

export interface UseChatStreamOptions {
  endpoint: string;
  headers?: HeadersSource;
  buildBody: (ctx: BuildBodyContext) => unknown;
  fetchImpl?: typeof fetch;
  poll?: PollOptions;
  firstByteTimeoutMs?: number;
  retryDelaysMs?: readonly number[];
  conversationId?: string | null;
  initialMessages?: ChatMessage[];
  onEvent?: (event: ChatEvent) => void;
  onDone?: (message: ChatMessage) => void;
  createId?: () => string;
}

export interface UseChatStreamResult {
  messages: ChatMessage[];
  status: StreamStatus;
  conversationId: string | null;
  /** Text of the in-flight assistant message ("" when idle). */
  streamingText: string;
  citations: Citation[];
  action: ChatAction | undefined;
  error: ErrorPayload | null;
  isBusy: boolean;
  send: (text: string, options?: SendOptions) => void;
  stop: () => void;
  retry: () => void;
  regenerate: () => void;
  reset: (messages?: ChatMessage[], conversationId?: string | null) => void;
  /** Adds a message that arrived outside a turn (a staff reply); no-op when already present. */
  append: (message: ChatMessage) => void;
  /** A person joined the conversation: the workflow's open cards close (their run ended on the server). */
  closeWorkflowCards: () => void;
  /** Turns a message's booking / lead card into its result, or marks it expired / unavailable. */
  setActionState: (messageId: string, state: CardState, result?: ResultCard) => void;
  /** Adds assistant messages that exist before any turn (a started workflow run); ones already present are skipped. */
  insertMessages: (messages: ChatMessage[]) => void;
}

const BUSY: ReadonlySet<StreamStatus> = new Set(["connecting", "streaming", "reconnecting", "polling"]);

function defaultId(): string {
  return globalThis.crypto.randomUUID();
}

function scheduleFrame(callback: () => void): void {
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(callback);
  else setTimeout(callback, 0);
}

/** Token batching: tokens are coalesced per animation frame; other events flush first. */
function useTokenBuffer(dispatch: (text: string) => void) {
  const pending = useRef("");
  const scheduled = useRef(false);
  const flush = useCallback(() => {
    scheduled.current = false;
    const text = pending.current;
    pending.current = "";
    if (text) dispatch(text);
  }, [dispatch]);
  const push = useCallback(
    (text: string) => {
      pending.current += text;
      if (scheduled.current) return;
      scheduled.current = true;
      scheduleFrame(flush);
    },
    [flush],
  );
  return { push, flush };
}

function lastUser(state: ChatState): ChatMessage | undefined {
  return state.messages.findLast((m) => m.role === "user");
}

/** SSE-over-fetch chat engine (spec §5.2): stream, resume, poll fallback, stop / retry / regenerate. */
export function useChatStream(options: UseChatStreamOptions): UseChatStreamResult {
  const [state, dispatch] = useReducer(chatReducer, undefined, () => initialChatState(options.initialMessages, options.conversationId ?? null));
  const optionsRef = useRef(options);
  const stateRef = useRef(state);
  const controllerRef = useRef<AbortController | null>(null);
  const awaitingDoneRef = useRef<string | null>(null);
  useEffect(() => {
    optionsRef.current = options;
    stateRef.current = state;
    const awaited = awaitingDoneRef.current;
    const finished = awaited ? state.messages.find((m) => m.id === awaited && m.status === "done") : undefined;
    if (finished) {
      awaitingDoneRef.current = null;
      options.onDone?.(finished);
    }
  });
  const dispatchTokens = useCallback((text: string) => {
    dispatch({ type: "tokens", text });
  }, []);
  const tokens = useTokenBuffer(dispatchTokens);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const settle = useCallback(
    (result: StreamResult, controller: AbortController) => {
      tokens.flush();
      if (controllerRef.current === controller) controllerRef.current = null;
      if (result.outcome === "error" && !result.fromEvent) dispatch({ type: "fail", error: result.error, unsent: !result.receivedAny });
    },
    [tokens],
  );

  const start = useCallback(
    (text: string, appendUser: boolean, workflowReply?: WorkflowReply, turnstileToken?: string) => {
      const opts = optionsRef.current;
      const createId = opts.createId ?? defaultId;
      const assistantId = createId();
      const clientMessageId = createId();
      dispatch({ type: "start", assistantId, user: appendUser ? { id: createId(), text, workflowReply } : undefined });
      awaitingDoneRef.current = assistantId;
      const controller = new AbortController();
      controllerRef.current = controller;
      const body = opts.buildBody({ text, conversationId: stateRef.current.conversationId, clientMessageId, workflowReply, ...(turnstileToken ? { turnstileToken } : {}) });
      const handlers = {
        onEvent: (event: ChatEvent) => {
          optionsRef.current.onEvent?.(event);
          if (event.type === "token") {
            tokens.push(event.data.text);
            return;
          }
          tokens.flush();
          dispatch({ type: "event", event });
        },
        onStatus: (status: StreamStatus) => {
          tokens.flush();
          dispatch({ type: "status", status });
        },
      };
      void runChatStream(opts, { body, requestId: clientMessageId }, handlers, controller.signal).then((result) => {
        settle(result, controller);
      });
    },
    [settle, tokens],
  );

  const send = useCallback(
    (raw: string, sendOptions?: SendOptions) => {
      const text = raw.trim();
      if (!text || BUSY.has(stateRef.current.status) || controllerRef.current) return;
      start(text, true, sendOptions?.workflowReply, sendOptions?.turnstileToken);
    },
    [start],
  );

  const stop = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller) return;
    controllerRef.current = null;
    controller.abort();
    tokens.flush();
    dispatch({ type: "stop" });
  }, [tokens]);

  const retry = useCallback(() => {
    if (controllerRef.current) return;
    const user = lastUser(stateRef.current);
    if (!user) return;
    dispatch({ type: "truncate", fromId: user.id });
    start(user.text, true, user.workflowReply);
  }, [start]);

  const regenerate = useCallback(() => {
    if (controllerRef.current) return;
    const current = stateRef.current;
    const user = lastUser(current);
    const assistant = current.messages.findLast((m) => m.role === "assistant");
    if (!user || !assistant || current.messages.indexOf(assistant) < current.messages.indexOf(user)) return;
    dispatch({ type: "truncate", fromId: assistant.id });
    start(user.text, false, user.workflowReply);
  }, [start]);

  const reset = useCallback((messages?: ChatMessage[], conversationId?: string | null) => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    dispatch({ type: "reset", messages, conversationId });
  }, []);

  const append = useCallback((message: ChatMessage) => {
    dispatch({ type: "append", message });
  }, []);

  const closeWorkflowCards = useCallback(() => {
    dispatch({ type: "settleCards" });
  }, []);

  const setActionState = useCallback((messageId: string, cardState: CardState, result?: ResultCard) => {
    dispatch({ type: "actionState", messageId, state: cardState, result });
  }, []);

  const insertMessages = useCallback((messages: ChatMessage[]) => {
    dispatch({ type: "insert", messages });
  }, []);

  const active = latestAssistant(state);
  return {
    messages: state.messages,
    status: state.status,
    conversationId: state.conversationId,
    streamingText: state.activeId ? (active?.text ?? "") : "",
    citations: active?.citations ?? [],
    action: active?.action,
    error: state.error,
    isBusy: BUSY.has(state.status),
    send,
    stop,
    retry,
    regenerate,
    reset,
    append,
    closeWorkflowCards,
    setActionState,
    insertMessages,
  };
}
